import { useEffect, useState, useMemo } from "react";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, X, FlaskConical, CheckCircle2, XCircle, AlertTriangle, Search, ExternalLink } from "lucide-react";

const RESULT_STYLE = {
  pass: "bg-emerald-100 text-emerald-800",
  fail: "bg-red-100 text-red-700",
  conditional_pass: "bg-amber-100 text-amber-800",
  pending: "bg-zinc-100 text-zinc-700",
};

export default function QualityTesting() {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSubmit, setShowSubmit] = useState(false);
  const [form, setForm] = useState({ test_type: "material", item_name: "", test_name: "", lab: "", parameters: "", document_url: "", verdict: "pending", material_master_id: "", garment_style_id: "" });
  const [designs, setDesigns] = useState([]);
  const [saving, setSaving] = useState(false);

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [verdictFilter, setVerdictFilter] = useState("all");

  // Detail panel
  const [selectedTest, setSelectedTest] = useState(null);

  // Batch operations
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [batchLoading, setBatchLoading] = useState(false);

  const designMap = useMemo(() => {
    const m = {};
    for (const d of designs) {
      m[d.id] = d.title || d.name || d.id;
    }
    return m;
  }, [designs]);

  const load = () => {
    api.get("/test-results")
      .then((r) => setTests(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    api.get("/designs").then((r) => setDesigns(r.data)).catch(() => {});
  }, []);

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        test_type: form.test_type,
        verdict: form.verdict,
        ingestion_channel: "manual",
        source_lab: form.lab,
        key_values: { item_name: form.item_name, test_name: form.test_name, parameters: form.parameters },
      };
      if (form.document_url) payload.document_url = form.document_url;
      if (form.material_master_id) payload.material_master_id = form.material_master_id;
      if (form.garment_style_id) payload.garment_style_id = form.garment_style_id;
      await api.post("/test-results", payload);
      toast.success("Test submitted");
      setShowSubmit(false);
      setForm({ test_type: "material", item_name: "", test_name: "", lab: "", parameters: "", document_url: "", verdict: "pending", material_master_id: "", garment_style_id: "" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const requestWaiver = async (id) => {
    try {
      await api.post(`/test-results/${id}/waiver`);
      toast.success("Waiver requested");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const approveWaiver = async (id) => {
    try {
      await api.patch(`/test-results/${id}/waiver`, { status: "approved" });
      toast.success("Waiver approved");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const batchApproveWaivers = async () => {
    setBatchLoading(true);
    let success = 0;
    let failed = 0;
    for (const id of selectedIds) {
      try {
        await api.patch(`/test-results/${id}/waiver`, { status: "approved" });
        success++;
      } catch {
        failed++;
      }
    }
    if (success > 0) toast.success(`${success} waiver(s) approved`);
    if (failed > 0) toast.error(`${failed} waiver(s) failed to approve`);
    setSelectedIds(new Set());
    setBatchLoading(false);
    load();
  };

  const resultIcon = (result) => {
    if (result === "pass") return <CheckCircle2 size={14} className="text-emerald-600" />;
    if (result === "fail") return <XCircle size={14} className="text-red-600" />;
    if (result === "conditional_pass") return <AlertTriangle size={14} className="text-amber-600" />;
    return null;
  };

  // Filter logic
  const filteredTests = useMemo(() => {
    let result = tests;
    if (typeFilter !== "all") {
      result = result.filter((t) => t.test_type === typeFilter);
    }
    if (verdictFilter !== "all") {
      result = result.filter((t) => t.verdict === verdictFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter((t) => {
        const itemName = (t.key_values?.item_name || t.item_name || "").toLowerCase();
        const testName = (t.key_values?.test_name || t.test_name || "").toLowerCase();
        const lab = (t.source_lab || t.lab || "").toLowerCase();
        return itemName.includes(q) || testName.includes(q) || lab.includes(q);
      });
    }
    return result;
  }, [tests, typeFilter, verdictFilter, searchQuery]);

  // Waiver-pending tests in current selection
  const waiverPendingSelected = useMemo(() => {
    return [...selectedIds].filter((id) => {
      const t = tests.find((x) => x.id === id);
      return t && t.waiver_justification && !t.waiver_by;
    });
  }, [selectedIds, tests]);

  const toggleSelection = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === filteredTests.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredTests.map((t) => t.id)));
    }
  };

  const abbreviateUuid = (uuid) => {
    if (!uuid) return "-";
    if (uuid.length > 12) return uuid.slice(0, 8) + "...";
    return uuid;
  };

  const resolveReference = (t) => {
    if (t.garment_style_id && designMap[t.garment_style_id]) {
      return designMap[t.garment_style_id];
    }
    if (t.garment_style_id) {
      return abbreviateUuid(t.garment_style_id);
    }
    if (t.material_master_id) {
      return abbreviateUuid(t.material_master_id);
    }
    return null;
  };

  return (
    <div className="p-10 max-w-6xl mx-auto">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-zinc-400">PLM</p>
          <h1 className="font-heading text-3xl font-bold mt-2 tracking-tight">Quality & Testing</h1>
          <p className="text-zinc-500 mt-2 text-sm">Submit tests, review results and manage waivers for materials and garments.</p>
        </div>
        <button onClick={() => setShowSubmit(true)}
          className="inline-flex items-center gap-2 bg-zinc-950 text-white px-5 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors">
          <Plus size={15} /> Submit test
        </button>
      </div>

      {/* Summary Cards */}
      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        {["pass", "fail", "conditional_pass", "pending"].map((r) => (
          <div key={r} className="border border-zinc-200 bg-white rounded-lg p-4">
            <div className="flex items-center gap-2">
              {resultIcon(r)}
              <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 capitalize">{r.replace("_", " ")}</p>
            </div>
            <p className="text-2xl font-bold mt-1">{tests.filter((t) => t.verdict === r).length}</p>
          </div>
        ))}
      </div>

      {/* Search and Filters */}
      {!loading && tests.length > 0 && (
        <div className="mt-6 flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs text-zinc-500 mb-1 block">Search</label>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search by item, test, or lab..."
                className="w-full border border-zinc-300 rounded-md pl-9 pr-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
            </div>
          </div>
          <div>
            <label className="text-xs text-zinc-500 mb-1 block">Type</label>
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
              className="border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
              <option value="all">All Types</option>
              <option value="material">Material</option>
              <option value="garment">Garment</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-zinc-500 mb-1 block">Verdict</label>
            <select value={verdictFilter} onChange={(e) => setVerdictFilter(e.target.value)}
              className="border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
              <option value="all">All Verdicts</option>
              <option value="pass">Pass</option>
              <option value="fail">Fail</option>
              <option value="conditional_pass">Conditional Pass</option>
              <option value="pending">Pending</option>
            </select>
          </div>
        </div>
      )}

      {/* Batch action bar */}
      {waiverPendingSelected.length > 0 && (
        <div className="mt-4 flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg px-5 py-3">
          <p className="text-sm text-amber-800">{waiverPendingSelected.length} waiver-pending test(s) selected</p>
          <button onClick={batchApproveWaivers} disabled={batchLoading}
            className="ml-auto inline-flex items-center gap-1.5 bg-amber-700 text-white px-4 py-2 rounded-md text-xs font-medium hover:bg-amber-800 transition-colors disabled:opacity-40">
            <CheckCircle2 size={13} />
            {batchLoading ? "Approving..." : "Batch Approve Waivers"}
          </button>
        </div>
      )}

      {loading ? (
        <div className="mt-10 text-sm text-zinc-400">Loading...</div>
      ) : tests.length === 0 ? (
        <div className="mt-10 border-2 border-dashed border-zinc-200 rounded-lg p-16 text-center text-zinc-400">
          <FlaskConical size={26} className="mx-auto mb-3" />
          <p className="text-sm">No test results yet.</p>
        </div>
      ) : filteredTests.length === 0 ? (
        <div className="mt-8 border-2 border-dashed border-zinc-200 rounded-lg p-12 text-center text-zinc-400">
          <Search size={22} className="mx-auto mb-3" />
          <p className="text-sm">No tests match your filters.</p>
        </div>
      ) : (
        <div className="mt-8 flex gap-0">
          {/* Table */}
          <div className={`border border-zinc-200 bg-white rounded-lg overflow-hidden ${selectedTest ? "flex-1 min-w-0" : "w-full"}`}>
            <div className="grid grid-cols-12 px-6 py-3 border-b border-zinc-100 text-[11px] uppercase tracking-[0.15em] text-zinc-400">
              <span className="col-span-1 flex items-center">
                <input type="checkbox" checked={selectedIds.size === filteredTests.length && filteredTests.length > 0} onChange={toggleAll}
                  className="rounded border-zinc-300 text-zinc-900 focus:ring-zinc-950" />
              </span>
              <span className="col-span-1">Type</span>
              <span className="col-span-2">Item</span>
              <span className="col-span-2">Test</span>
              <span className="col-span-1">Lab</span>
              <span className="col-span-2">Ref</span>
              <span className="col-span-1">Result</span>
              <span className="col-span-2 text-right">Actions</span>
            </div>
            {filteredTests.map((t) => (
              <div key={t.id}
                className={`grid grid-cols-12 items-center px-6 py-4 border-b border-zinc-50 cursor-pointer hover:bg-zinc-50 transition-colors ${selectedTest?.id === t.id ? "bg-zinc-50" : ""}`}
                onClick={() => setSelectedTest(selectedTest?.id === t.id ? null : t)}>
                <span className="col-span-1" onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" checked={selectedIds.has(t.id)} onChange={() => toggleSelection(t.id)}
                    className="rounded border-zinc-300 text-zinc-900 focus:ring-zinc-950" />
                </span>
                <span className="col-span-1 text-[10px] uppercase text-zinc-400">{t.test_type}</span>
                <span className="col-span-2 text-sm font-medium truncate">{t.key_values?.item_name || t.item_name || "-"}</span>
                <span className="col-span-2 text-sm text-zinc-600 truncate">{t.key_values?.test_name || t.test_name || "-"}</span>
                <span className="col-span-1 text-sm text-zinc-600 truncate">{t.source_lab || t.lab || "N/A"}</span>
                <span className="col-span-2 text-xs text-zinc-500 truncate" title={t.garment_style_id || t.material_master_id || ""}>
                  {resolveReference(t) || "-"}
                </span>
                <span className="col-span-1">
                  <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium ${RESULT_STYLE[t.verdict] || RESULT_STYLE.pending}`}>
                    {resultIcon(t.verdict)} {t.verdict.replace("_", " ")}
                  </span>
                </span>
                <span className="col-span-2 text-right" onClick={(e) => e.stopPropagation()}>
                  {t.verdict === "fail" && !t.waiver_justification && (
                    <button onClick={() => requestWaiver(t.id)}
                      className="text-xs text-amber-700 hover:text-amber-900 font-medium">Request waiver</button>
                  )}
                  {t.waiver_justification && !t.waiver_by && (
                    <button onClick={() => approveWaiver(t.id)}
                      className="text-xs text-emerald-700 hover:text-emerald-900 font-medium">Approve waiver</button>
                  )}
                  {t.waiver_justification && t.waiver_by && (
                    <span className="text-[10px] text-emerald-600 font-medium">Waiver approved</span>
                  )}
                </span>
              </div>
            ))}
          </div>

          {/* Detail Panel (sidebar) */}
          {selectedTest && (
            <div className="w-80 flex-shrink-0 ml-4 border border-zinc-200 bg-white rounded-lg overflow-hidden self-start">
              <div className="px-5 py-4 border-b border-zinc-100 flex items-center justify-between">
                <h3 className="text-sm font-semibold">Test Details</h3>
                <button onClick={() => setSelectedTest(null)} className="p-1 hover:bg-zinc-100 rounded-md"><X size={14} /></button>
              </div>
              <div className="px-5 py-4 space-y-4 text-sm max-h-[70vh] overflow-y-auto">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-1">Type</p>
                  <p className="text-sm capitalize">{selectedTest.test_type}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-1">Verdict</p>
                  <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium ${RESULT_STYLE[selectedTest.verdict] || RESULT_STYLE.pending}`}>
                    {resultIcon(selectedTest.verdict)} {selectedTest.verdict.replace("_", " ")}
                  </span>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-1">Lab</p>
                  <p className="text-sm">{selectedTest.source_lab || selectedTest.lab || "N/A"}</p>
                </div>

                {/* Key Values */}
                {selectedTest.key_values && Object.keys(selectedTest.key_values).length > 0 && (
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-2">Key Values</p>
                    <div className="space-y-2">
                      {Object.entries(selectedTest.key_values).map(([k, v]) => (
                        <div key={k} className="flex justify-between">
                          <span className="text-xs text-zinc-500 capitalize">{k.replace(/_/g, " ")}</span>
                          <span className="text-xs font-medium text-right max-w-[160px] truncate">{v || "-"}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Document URL */}
                {selectedTest.document_url && (
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-1">Document</p>
                    <a href={selectedTest.document_url} target="_blank" rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:text-blue-800 inline-flex items-center gap-1 break-all">
                      <ExternalLink size={12} /> {selectedTest.document_url.length > 40 ? selectedTest.document_url.slice(0, 40) + "..." : selectedTest.document_url}
                    </a>
                  </div>
                )}

                {/* Reference */}
                {resolveReference(selectedTest) && (
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-1">
                      {selectedTest.garment_style_id ? "Garment Style" : "Material Master"}
                    </p>
                    <p className="text-sm">{resolveReference(selectedTest)}</p>
                  </div>
                )}

                {/* Waiver Status */}
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-1">Waiver Status</p>
                  {selectedTest.waiver_justification ? (
                    <div className="space-y-1">
                      <span className={`inline-flex text-[10px] px-2 py-0.5 rounded-full font-medium ${selectedTest.waiver_by ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                        {selectedTest.waiver_by ? "Approved" : "Pending Approval"}
                      </span>
                      {selectedTest.waiver_justification && (
                        <p className="text-xs text-zinc-600 mt-1">{selectedTest.waiver_justification}</p>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-zinc-400">No waiver requested</p>
                  )}
                </div>

                {/* Created Date */}
                {selectedTest.created_at && (
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-1">Created</p>
                    <p className="text-sm">{new Date(selectedTest.created_at).toLocaleString()}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {showSubmit && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setShowSubmit(false)}>
          <div className="bg-white rounded-lg p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-semibold text-lg">Submit test</h2>
              <button onClick={() => setShowSubmit(false)} className="p-1.5 hover:bg-zinc-100 rounded-md"><X size={16} /></button>
            </div>
            <div className="space-y-4">
              <select value={form.test_type} onChange={(e) => setForm({ ...form, test_type: e.target.value })}
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
                <option value="material">Material Test</option>
                <option value="garment">Garment Test</option>
              </select>
              <input value={form.item_name} onChange={(e) => setForm({ ...form, item_name: e.target.value })} placeholder="Item name"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <input value={form.test_name} onChange={(e) => setForm({ ...form, test_name: e.target.value })} placeholder="Test name (e.g. Colour fastness)"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <input value={form.lab} onChange={(e) => setForm({ ...form, lab: e.target.value })} placeholder="Lab name (optional)"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <textarea value={form.parameters} onChange={(e) => setForm({ ...form, parameters: e.target.value })} rows={3} placeholder="Test parameters / standards"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950 resize-none" />
              <input value={form.document_url} onChange={(e) => setForm({ ...form, document_url: e.target.value })} placeholder="Document URL (optional)"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <select value={form.verdict} onChange={(e) => setForm({ ...form, verdict: e.target.value })}
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
                <option value="pending">Pending</option>
                <option value="pass">Pass</option>
                <option value="fail">Fail</option>
                <option value="conditional_pass">Conditional Pass</option>
              </select>
              {form.test_type === "material" ? (
                <input value={form.material_master_id} onChange={(e) => setForm({ ...form, material_master_id: e.target.value })} placeholder="Material Master ID (optional UUID)"
                  className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              ) : (
                <select value={form.garment_style_id} onChange={(e) => setForm({ ...form, garment_style_id: e.target.value })}
                  className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
                  <option value="">Link to style (optional)</option>
                  {designs.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
                </select>
              )}
              <button onClick={submit} disabled={!form.item_name || !form.test_name || saving}
                className="w-full bg-zinc-950 text-white py-3 rounded-md font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40">
                {saving ? "Submitting..." : "Submit test"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
